// modules/MultiplayerPlayerManager/PlayerRenderer.js
// 多人玩家渲染模块 - 负责玩家精灵和UI的创建

import DebugHelper from '../DebugHelper.js';
import {
    ensurePlayerAnimations,
    getPlayerAnimKey,
    getPlayerStaticFrame
} from '../PlayerSpriteFrames.js';

export default class PlayerRenderer {
    constructor(scene) {
        this.scene = scene;
        this.remotePlayers = new Map(); // 存储远程玩家精灵 {playerId: sprite}
        this.playerNameTags = new Map(); // 存储玩家名称标签 {playerId: text}
    }
    
    /**
     * 添加远程玩家
     * @param {string} playerId - 玩家ID
     * @param {Object} playerData - 玩家数据
     * @returns {Object} 创建的玩家对象 {sprite, nameTag}
     */
    addRemotePlayer(playerId, playerData) {
        // 添加调试日志，查看接收到的数据
        DebugHelper.log(`addRemotePlayer 接收数据: playerId=${playerId}, playerData=`, playerData);
        
        // 获取玩家名称和角色形象
        // 兼容两种数据结构：
        // 1. playerData.playerName 存在（新玩家加入时）
        // 2. playerData 是完整的玩家对象，包含 id 和 playerName（初始化已存在的玩家时）
        const playerName = playerData?.playerName || '玩家';
        const element = playerData?.element || 'player1';
        
        DebugHelper.log(`addRemotePlayer 解析结果: playerName=${playerName}, element=${element}`);
        
        if (this.remotePlayers.has(playerId)) {
            const sprite = this.remotePlayers.get(playerId);
            const nameTag = this.playerNameTags.get(playerId);
            
            // 只有在 playerData 中包含这些字段时才更新，避免用不完整数据覆盖完整数据
            // 如果 playerData 中只有位置数据（x, y, direction），不应该更新名字和形象
            if (sprite) {
                // 检查 playerData 是否包含完整的玩家信息（不仅仅是位置更新）
                // 如果包含多个字段（除了 x, y, direction），说明是完整的玩家数据
                const hasPlayerInfo = playerData && Object.keys(playerData).filter(
                    key => !['x', 'y', 'direction'].includes(key)
                ).length > 0;
                
                // 只有当 playerData 中包含完整信息时才更新名字和形象
                if (hasPlayerInfo) {
                    // 更新名字（如果提供了）
                    if (playerData.playerName !== undefined && playerData.playerName !== null) {
                        sprite.setData('playerName', playerData.playerName);
                        DebugHelper.log(`玩家 ${playerId} 已存在，更新名字: ${playerData.playerName}`);
                        
                        // 更新名称标签
                        if (nameTag) {
                            nameTag.setText(playerData.playerName);
                        }
                    }
                    
                    // 更新形象（如果提供了）
                    // 注意：只有当 playerData 中明确包含 element 字段，且值有效时才更新
                    // 避免从位置更新数据中错误地更新形象
                    if (playerData.element !== undefined && playerData.element !== null && playerData.element !== '') {
                        // 检查是否是有效的地图键（element 应该是精灵纹理的键）
                        // 如果 element 在 playerData 的顶层且不是从本地玩家数据继承来的，才更新
                        const currentElement = sprite.texture ? sprite.texture.key : null;
                        if (currentElement !== playerData.element) {
                            DebugHelper.log(`玩家 ${playerId} 已存在，更新形象: ${currentElement} -> ${playerData.element}, playerData keys:`, Object.keys(playerData));
                            sprite.setTexture(playerData.element);
                        }
                    }
                } else {
                    // 如果只有位置数据，不应该更新形象
                    DebugHelper.log(`玩家 ${playerId} 已存在，但 playerData 只包含位置数据，不更新形象。playerData:`, playerData);
                }
                
                // 方向可以随时更新（位置更新也包含方向）
                if (playerData?.direction) {
                    sprite.setData('direction', playerData.direction);
                }
            }
            
            return { sprite, nameTag };
        }
        
        try {
            // 创建玩家精灵（使用physics sprite）
            const sprite = this.scene.physics.add.sprite(
                playerData?.x || 400,
                playerData?.y || 400,
                element
            );
            
            // 设置物理体大小和偏移（与本地玩家保持一致）
            const texture = this.scene.textures.get(element);
            const frame = texture?.get();
            const frameW = frame?.width || 32;
            if (frameW > 64) {
                const scale = 32 / frameW;
                sprite.setScale(scale);
                const hit = Math.round(28 / scale);
                sprite.setSize(hit, hit);
                sprite.setOffset(Math.round(2 / scale), Math.round(2 / scale));
            } else {
                sprite.setSize(28, 28);
                sprite.setOffset(2, 2);
            }
            sprite.setDepth(10);
            
            // 设置玩家数据
            sprite.setData('playerId', playerId);
            sprite.setData('playerName', playerName);
            sprite.setData('direction', playerData?.direction || 'down');
            
            // 创建名称标签
            const nameTag = this.createNameTag(sprite, playerName);
            
            this.remotePlayers.set(playerId, sprite);
            this.playerNameTags.set(playerId, nameTag);
            
            DebugHelper.log(`添加远程玩家: ${playerId} (${playerName}), element: ${element}`);
            
            return { sprite, nameTag };
        } catch (error) {
            DebugHelper.error(`添加远程玩家失败: ${playerId}`, error);
            return { sprite: null, nameTag: null };
        }
    }
    
    /**
     * 创建名称标签
     * @param {Phaser.GameObjects.Sprite} sprite - 玩家精灵
     * @param {string} playerName - 玩家名称
     * @returns {Phaser.GameObjects.Text} 名称标签
     */
    createNameTag(sprite, playerName) {
        const nameTag = this.scene.add.text(
            sprite.x,
            sprite.y - 30,
            playerName,
            {
                fontSize: '12px',
                fill: '#4cc9f0',
                fontFamily: 'Arial',
                backgroundColor: '#000000',
                padding: { x: 4, y: 2 }
            }
        );
        nameTag.setOrigin(0.5);
        nameTag.setDepth(11);
        nameTag.setScrollFactor(1);
        return nameTag;
    }
    
    /**
     * 更新名称标签
     * @param {string} playerId - 玩家ID
     * @param {string} playerName - 玩家名称
     */
    updateNameTag(playerId, playerName) {
        const nameTag = this.playerNameTags.get(playerId);
        if (nameTag) {
            nameTag.setText(playerName);
        }
    }
    
    /**
     * 更新名称标签位置
     * @param {string} playerId - 玩家ID
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     */
    updateNameTagPosition(playerId, x, y) {
        const nameTag = this.playerNameTags.get(playerId);
        if (nameTag) {
            nameTag.x = x;
            nameTag.y = y - 30;
        }
    }
    
    /**
     * 移除远程玩家
     * @param {string} playerId - 玩家ID
     */
    removeRemotePlayer(playerId) {
        const sprite = this.remotePlayers.get(playerId);
        const nameTag = this.playerNameTags.get(playerId);
        
        if (sprite) {
            sprite.destroy();
            this.remotePlayers.delete(playerId);
        }
        
        if (nameTag) {
            nameTag.destroy();
            this.playerNameTags.delete(playerId);
        }
        
        DebugHelper.log(`移除远程玩家: ${playerId}`);
    }
    
    /**
     * 获取远程玩家精灵
     * @param {string} playerId - 玩家ID
     * @returns {Phaser.GameObjects.Sprite|null} 玩家精灵
     */
    getPlayerSprite(playerId) {
        return this.remotePlayers.get(playerId) || null;
    }
    
    /**
     * 获取所有远程玩家
     * @returns {Array} 玩家列表
     */
    getRemotePlayers() {
        return Array.from(this.remotePlayers.values());
    }
    
    /**
     * 更新远程玩家动画
     * @param {Phaser.GameObjects.Sprite} sprite - 玩家精灵
     * @param {string} playerId - 玩家ID
     * @param {string} direction - 移动方向
     * @param {boolean} isMoving - 是否在移动
     */
    updateRemotePlayerAnimation(sprite, playerId, direction, isMoving) {
        if (!sprite || !sprite.anims) return;
        
        // 获取精灵的当前纹理键（每个远程玩家可能有不同的纹理）
        const textureKey = sprite.texture ? sprite.texture.key : null;
        if (!textureKey) return;
        
        // 为每个纹理创建唯一的动画键（避免不同玩家使用相同的动画导致纹理混乱）
        const animMap = {
            'up': getPlayerAnimKey(textureKey, 'up'),
            'down': getPlayerAnimKey(textureKey, 'down'),
            'left': getPlayerAnimKey(textureKey, 'left'),
            'right': getPlayerAnimKey(textureKey, 'right')
        };
        
        ensurePlayerAnimations(this.scene, textureKey);
        
        if (isMoving) {
            // 播放移动动画
            const targetAnim = animMap[direction];
            if (targetAnim && this.scene.anims.exists(targetAnim)) {
                if (!sprite.anims.isPlaying || sprite.anims.currentAnim?.key !== targetAnim) {
                    sprite.play(targetAnim, true);
                }
            }
        } else {
            // 停止动画，显示静态帧
            sprite.anims.stop();
            sprite.setFrame(getPlayerStaticFrame(textureKey, direction));
        }
    }
    
    /**
     * 清理所有远程玩家
     */
    destroy() {
        this.remotePlayers.forEach((sprite, playerId) => {
            this.removeRemotePlayer(playerId);
        });
        this.remotePlayers.clear();
        this.playerNameTags.clear();
    }
}

